import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Building2 } from "lucide-react";
import AnimatedSection from "../common/AnimatedSection";
import SectionHeading from "../common/SectionHeading";
import Button from "../common/Button";

const segments = {
  b2c: {
    title: "For Homeowners",
    subtitle: "Your home, your way",
    description:
      "Whether you're moving into your first apartment or renovating your dream villa, we understand what you need. We work with your budget, timeline, and style to create spaces you'll love coming home to.",
    featureSections: [
      {
        title: "Complete Storage Solution",
        features: [
          "Complete Storage Solution – From Kitchen to Wardrobe",
          "Wall Designing",
          "False Ceiling",
          "Beds, Sofas & Consoles",
          "Study / Office Furnitures",
          "Balcony Makeovers",
          "Home Appliances",
          "Home Automation",
          "Furnishings",
        ],
      },
      {
        title: "Budget Friendly Solutions",
        features: [
          "Budget Friendly Storage Solutions",
          "Extra Storage Ideas",
          "Rental-friendly packages starting at ₹3 Lakhs (ready in 20 days)",
          "500+ Residential Homes",
          "Design Accents - Indian Regional Specific to Modern Homes",
        ],
      },
    ],
    removedFeatures: [
      "Complete solutions – from kitchen to wardrobe",
      "Rental-friendly packages starting at \u20b93 Lakhs (ready in 20 days)",
      "Full-home transformations (completed in 45 days)",
      "Designs that match your personality",
      "Quality that lasts – 10-year warranty",
    ],
    cta: "Tell us about your home",
    link: "/contact?type=b2c",
    image:
      "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&q=80",
    icon: Home,
  },
  b2b: {
    title: "For Builders & Architects",
    subtitle: "Partners in creating great spaces",
    description:
      "We know you need reliable partners who understand scale and deadlines. Our factory and team are built to support your projects, delivering consistent quality that makes your developments stand out.",
    features: [
      "Bulk solutions for doors and kitchens",
      "Model homes and clubhouse interiors",
      "Custom job works and pressed panels",
      "Scalable production and installation",
      "Dedicated support for your projects",
    ],
    cta: "Let's discuss your project",
    link: "/contact?type=b2b",
    image:
      "https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80",
    icon: Building2,
  },
};

export default function B2CB2BSplit() {
  const [activeTab, setActiveTab] = useState("b2c");
  const segment = segments[activeTab];

  return (
    <section className="section-spacing bg-background-main">
      <div className="container-custom section-padding">
        <SectionHeading
          label="We're Here For You"
          title="Solutions for every home and project."
          align="center"
        />

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white rounded-full p-1.5 shadow-soft">
            {Object.entries(segments).map(([key, seg]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === key
                    ? "bg-brand-accent text-white shadow-md"
                    : "text-text-secondary hover:text-brand-primary"
                }`}
              >
                <seg.icon className="w-4 h-4" />
                {seg.title}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <AnimatedSection key={activeTab} animation="fade">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden">
                <img
                  src={segment.image}
                  alt={segment.title}
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-large p-6 max-w-[240px]">
                <segment.icon className="w-8 h-8 text-brand-accent mb-3" />
                <p className="font-heading text-lg text-brand-primary">
                  {activeTab === "b2c"
                    ? "Homes transformed since 2025"
                    : "Projects delivered with partners"}
                </p>
                <p className="text-3xl font-heading text-brand-accent mt-1">
                  {activeTab === "b2c" ? "500+" : "50+"}
                </p>
              </div>
            </div>

            {/* Content */}
            <div>
              <span className="text-brand-accent font-medium text-sm uppercase tracking-wider">
                {segment.subtitle}
              </span>
              <h3 className="font-heading text-h2 text-brand-primary mt-3 mb-6">
                {segment.title}
              </h3>
              <p className="text-text-secondary text-lg mb-8 leading-relaxed">
                {segment.description}
              </p>

              {/* Features - handle both old format and new section format */}
              {segment.featureSections ? (
                <div className="grid md:grid-cols-2 gap-6 mb-10">
                  {segment.featureSections.map((section, idx) => (
                    <div key={idx} className="bg-background-card rounded-2xl p-6">
                      <h4 className="font-heading text-lg text-brand-primary mb-4">
                        {section.title}
                      </h4>
                      <ul className="space-y-3">
                        {section.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-brand-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <svg
                                className="w-3.5 h-3.5 text-brand-accent"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                            <span className="text-text-primary text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <ul className="space-y-4 mb-10">
                  {segment.features?.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-brand-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg
                          className="w-3.5 h-3.5 text-brand-accent"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <span className="text-text-primary">{feature}</span>
                    </li>
                  ))}
                </ul>
              )}

              <Button to={segment.link} variant="primary" size="large">
                {segment.cta}
              </Button>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
